import { useEffect, useState } from 'react';
import adminApi from '@/lib/adminApi';

const emptyForm = {
  brand_name: '',
  headline: '',
  description: '',
  logo_url: '',
  link_url: '',
  placement_label: 'Brand Partner',
  display_order: 0,
  is_active: true,
};

const AdminSponsors = () => {
  const [sponsors, setSponsors] = useState([]);
  const [formData, setFormData] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [error, setError] = useState('');

  const loadSponsors = async () => {
    setLoading(true);
    try {
      const response = await adminApi.get('/admin/sponsors');
      setSponsors(response.data.sponsors || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSponsors();
  }, []);

  const updateField = (field, value) => {
    setFormData((current) => ({ ...current, [field]: value }));
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingId(null);
    setError('');
  };

  const submitSponsor = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');

    try {
      const payload = {
        ...formData,
        display_order: Number(formData.display_order || 0),
      };
      if (editingId) {
        await adminApi.put(`/admin/sponsors/${editingId}`, payload);
      } else {
        await adminApi.post('/admin/sponsors', payload);
      }
      resetForm();
      await loadSponsors();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Unable to save brand partner.');
    } finally {
      setSaving(false);
    }
  };

  const editSponsor = (sponsor) => {
    setEditingId(sponsor.id);
    setFormData({
      brand_name: sponsor.brand_name || '',
      headline: sponsor.headline || '',
      description: sponsor.description || '',
      logo_url: sponsor.logo_url || '',
      link_url: sponsor.link_url || '',
      placement_label: sponsor.placement_label || 'Brand Partner',
      display_order: sponsor.display_order || 0,
      is_active: Boolean(sponsor.is_active),
    });
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const deleteSponsor = async (sponsorId) => {
    if (!window.confirm('Delete this brand partner?')) return;
    await adminApi.delete(`/admin/sponsors/${sponsorId}`);
    await loadSponsors();
  };

  const uploadLogo = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    setError('');
    try {
      const payload = new FormData();
      payload.append('file', file);
      const response = await adminApi.post('/uploads/profile-image', payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      updateField('logo_url', response.data.image_url || '');
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Unable to upload logo image.');
    } finally {
      setUploadingLogo(false);
      event.target.value = '';
    }
  };

  return (
    <div className="space-y-6">
      <section className="rounded-[2rem] border border-orange-100 bg-white p-6 shadow-lg sm:p-8">
        <p className="text-sm font-semibold uppercase tracking-[0.25em] text-[#FF6F00]">Brand partners</p>
        <div className="mt-3 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Sponsors & brand ads</h2>
            <p className="mt-3 max-w-3xl text-gray-600">
              Add premium partner cards for the homepage. Keep only approved, relevant brands active.
            </p>
          </div>
          <button type="button" onClick={resetForm} className="btn-outline">
            Add New Partner
          </button>
        </div>
      </section>

      <form onSubmit={submitSponsor} className="rounded-[2rem] border border-orange-100 bg-white p-6 shadow-lg sm:p-8">
        <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-2xl font-bold text-gray-900">{editingId ? 'Edit brand partner' : 'Add brand partner'}</h3>
            <p className="text-gray-600">Brand name and headline are required. Logo and link are optional.</p>
          </div>
          {editingId && (
            <button type="button" onClick={resetForm} className="btn-outline">
              Cancel Edit
            </button>
          )}
        </div>

        <div className="grid gap-4 lg:grid-cols-3">
          <input
            value={formData.brand_name}
            onChange={(event) => updateField('brand_name', event.target.value)}
            placeholder="Brand name"
            className="rounded-2xl border border-gray-300 px-4 py-3"
            required
          />
          <input
            value={formData.headline}
            onChange={(event) => updateField('headline', event.target.value)}
            placeholder="Short headline"
            className="rounded-2xl border border-gray-300 px-4 py-3"
            required
          />
          <input
            value={formData.placement_label}
            onChange={(event) => updateField('placement_label', event.target.value)}
            placeholder="Brand Partner"
            className="rounded-2xl border border-gray-300 px-4 py-3"
          />
          <input
            value={formData.logo_url}
            onChange={(event) => updateField('logo_url', event.target.value)}
            placeholder="Logo image URL"
            className="rounded-2xl border border-gray-300 px-4 py-3"
          />
          <input
            value={formData.link_url}
            onChange={(event) => updateField('link_url', event.target.value)}
            placeholder="Website / campaign URL"
            className="rounded-2xl border border-gray-300 px-4 py-3"
          />
          <input
            type="number"
            value={formData.display_order}
            onChange={(event) => updateField('display_order', event.target.value)}
            placeholder="Display order"
            className="rounded-2xl border border-gray-300 px-4 py-3"
          />
        </div>

        <div className="mt-4 rounded-2xl border border-orange-100 bg-orange-50/60 p-4">
          <label className="block text-sm font-bold text-gray-900">Upload logo from PC or mobile</label>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={uploadLogo}
            className="mt-2 w-full text-sm text-gray-700"
            disabled={uploadingLogo}
          />
          <p className="mt-2 text-xs text-gray-500">
            JPG, PNG, or WebP. Admin uploads skip malware scan but still validate that the file is an image.
          </p>
          {uploadingLogo && <p className="mt-2 text-sm font-semibold text-[#FF6F00]">Uploading logo...</p>}
          {formData.logo_url && (
            <div className="mt-3 flex items-center gap-3">
              <div className="flex h-16 w-24 items-center justify-center rounded-2xl bg-white ring-1 ring-orange-100">
                <img src={formData.logo_url} alt="Brand logo preview" className="max-h-12 max-w-20 object-contain" />
              </div>
              <span className="break-all text-xs text-gray-500">{formData.logo_url}</span>
            </div>
          )}
        </div>

        <textarea
          value={formData.description}
          onChange={(event) => updateField('description', event.target.value)}
          placeholder="Short sponsor description"
          className="mt-4 min-h-[110px] w-full rounded-2xl border border-gray-300 px-4 py-3"
        />

        <label className="mt-4 inline-flex items-center gap-3 text-sm font-semibold text-gray-700">
          <input
            type="checkbox"
            checked={formData.is_active}
            onChange={(event) => updateField('is_active', event.target.checked)}
          />
          Active on homepage
        </label>

        {error && <p className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}

        <button type="submit" disabled={saving} className="btn-primary mt-5 disabled:cursor-not-allowed disabled:bg-gray-300">
          {saving ? 'Saving...' : editingId ? 'Save Partner Changes' : 'Create Partner'}
        </button>
      </form>

      <section className="rounded-[2rem] border border-orange-100 bg-white shadow-lg">
        {loading ? (
          <div className="p-6 text-gray-500">Loading brand partners...</div>
        ) : sponsors.length === 0 ? (
          <div className="p-6 text-gray-500">No brand partners added yet.</div>
        ) : (
          <div className="divide-y divide-orange-100">
            {sponsors.map((sponsor) => (
              <article key={sponsor.id} className="grid gap-5 p-6 lg:grid-cols-[160px_1fr_auto] lg:items-center">
                <div className="flex h-24 items-center justify-center rounded-3xl bg-orange-50">
                  {sponsor.logo_url ? (
                    <img src={sponsor.logo_url} alt={sponsor.brand_name} className="max-h-16 max-w-32 object-contain" />
                  ) : (
                    <span className="text-3xl font-bold text-[#FF6F00]">{sponsor.brand_name?.charAt(0) || 'B'}</span>
                  )}
                </div>
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-xl font-bold text-gray-900">{sponsor.brand_name}</h3>
                    <span className={`rounded-full px-3 py-1 text-xs font-bold ${sponsor.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700'}`}>
                      {sponsor.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <span className="rounded-full bg-purple-100 px-3 py-1 text-xs font-bold text-[#4A148C]">
                      Order {sponsor.display_order || 0}
                    </span>
                  </div>
                  <p className="mt-2 font-semibold text-[#4A148C]">{sponsor.headline}</p>
                  {sponsor.description && <p className="mt-1 text-sm text-gray-600">{sponsor.description}</p>}
                  {sponsor.link_url && (
                    <a href={sponsor.link_url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-sm font-semibold text-[#FF6F00]">
                      Open sponsor link
                    </a>
                  )}
                </div>
                <div className="flex flex-wrap gap-3 lg:justify-end">
                  <button type="button" onClick={() => editSponsor(sponsor)} className="btn-outline">
                    Edit
                  </button>
                  <button type="button" onClick={() => deleteSponsor(sponsor.id)} className="rounded-full border border-red-200 px-5 py-2 font-semibold text-red-600 hover:bg-red-50">
                    Delete
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminSponsors;
